/**
 * shared/programs.js — logic cho chương trình có hạn nộp.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * VÌ SAO TÁCH KHỎI QUỸ
 *
 * Quỹ nhận hồ sơ quanh năm. Chương trình thì mở rồi đóng theo đợt, và mỗi đợt
 * có điều kiện riêng: L-CAMP Vietnam 2026 khác L-CAMP Vietnam 2027, cohort 8
 * khác cohort 9.
 *
 * Nếu nhét chung vào danh sách quỹ thì sáu tháng sau website sẽ đầy chương
 * trình đã đóng mà không ai biết cái nào còn nhận hồ sơ. Đó là kiểu sai làm
 * mất niềm tin nhanh nhất — người dùng chuẩn bị hồ sơ mấy ngày rồi mới phát
 * hiện hạn nộp đã qua từ tháng trước.
 *
 * Nhưng chương trình đã đóng KHÔNG nên bị xoá. Biết trước kỳ sau mở khi nào
 * là thông tin có giá trị để chuẩn bị. Chúng chỉ cần hiện ở nhóm khác.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * HÀM THUẦN — không đọc đồng hồ hệ thống. Ngày hôm nay luôn được truyền vào,
 * để kiểm thử được và để kết quả không đổi giữa lúc build và lúc chạy.
 */

export const ENGINE_VERSION = '1.0.0';

/** Trạng thái một đợt nhận hồ sơ. */
export const TRANG_THAI = Object.freeze({
  DANG_MO: 'DANG_MO',
  SAP_MO: 'SAP_MO',
  DA_DONG: 'DA_DONG',
  LIEN_TUC: 'LIEN_TUC',
  CHUA_RO: 'CHUA_RO',
});

export const NHAN_TRANG_THAI = Object.freeze({
  DANG_MO: 'Đang nhận hồ sơ',
  SAP_MO: 'Sắp mở',
  DA_DONG: 'Đã đóng',
  LIEN_TUC: 'Nhận hồ sơ liên tục',
  CHUA_RO: 'Chưa rõ lịch',
});

const LA_NGAY = /^\d{4}-\d{2}-\d{2}$/;

/** So sánh hai chuỗi ngày YYYY-MM-DD mà không dùng Date — tránh lệch múi giờ. */
function truoc(a, b) {
  return a < b;
}

/**
 * Trạng thái của một đợt tại một ngày cho trước.
 *
 * @param {Object} dot   { opensAt, closesAt, rolling }
 * @param {string} homNay dạng YYYY-MM-DD
 */
export function trangThai(dot, homNay) {
  if (!LA_NGAY.test(String(homNay))) {
    throw new Error('homNay phải có dạng YYYY-MM-DD');
  }
  if (!dot) return TRANG_THAI.CHUA_RO;
  if (dot.rolling) return TRANG_THAI.LIEN_TUC;

  const mo = LA_NGAY.test(String(dot.opensAt)) ? dot.opensAt : null;
  const dong = LA_NGAY.test(String(dot.closesAt)) ? dot.closesAt : null;

  if (!mo && !dong) return TRANG_THAI.CHUA_RO;
  // Hạn nộp tính tới hết ngày đó, nên chỉ đóng khi hôm nay đã qua ngày hạn.
  if (dong && truoc(dong, homNay)) return TRANG_THAI.DA_DONG;
  if (mo && truoc(homNay, mo)) return TRANG_THAI.SAP_MO;
  return TRANG_THAI.DANG_MO;
}

/** Số ngày còn lại tới hạn nộp. Trả null khi không có hạn. */
export function conLai(dot, homNay) {
  if (!dot || !LA_NGAY.test(String(dot.closesAt))) return null;
  const d = (s) => Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10));
  return Math.round((d(dot.closesAt) - d(homNay)) / 86400000);
}

/**
 * Gắn trạng thái vào từng chương trình và sắp xếp theo thứ tự hữu ích:
 * đang mở trước (hạn gần nhất lên đầu), rồi sắp mở, liên tục, chưa rõ, đã đóng.
 */
const THU_TU = {
  [TRANG_THAI.DANG_MO]: 0,
  [TRANG_THAI.SAP_MO]: 1,
  [TRANG_THAI.LIEN_TUC]: 2,
  [TRANG_THAI.CHUA_RO]: 3,
  [TRANG_THAI.DA_DONG]: 4,
};

export function sapXep(danhSach, homNay) {
  return danhSach
    .map((ct) => {
      const tt = trangThai(ct.edition, homNay);
      return {
        ...ct,
        trangThai: tt,
        nhanTrangThai: NHAN_TRANG_THAI[tt],
        soNgayConLai: tt === TRANG_THAI.DANG_MO ? conLai(ct.edition, homNay) : null,
      };
    })
    .sort((a, b) => {
      const t = THU_TU[a.trangThai] - THU_TU[b.trangThai];
      if (t !== 0) return t;
      if (a.trangThai === TRANG_THAI.DANG_MO) {
        return (a.soNgayConLai ?? 9999) - (b.soNgayConLai ?? 9999);
      }
      return a.name.localeCompare(b.name, 'vi');
    });
}

/** Nhóm cho giao diện: đang nhận hồ sơ, và theo dõi cho kỳ sau. */
export function phanNhom(danhSach, homNay) {
  const ds = sapXep(danhSach, homNay);
  const dangMo = ds.filter(
    (x) => x.trangThai === TRANG_THAI.DANG_MO || x.trangThai === TRANG_THAI.LIEN_TUC
  );
  const theoDoi = ds.filter(
    (x) => x.trangThai !== TRANG_THAI.DANG_MO && x.trangThai !== TRANG_THAI.LIEN_TUC
  );
  return { dangMo, theoDoi };
}

export default { ENGINE_VERSION, TRANG_THAI, trangThai, conLai, sapXep, phanNhom };
